import { REPAYMENT_WEEKLY } from '../../utils/constants';

/**
 * Mixin for RefineSearchContainer form state management
 * Centralizes form state, watchers, initialization, and reset logic
 */
export default {
  data() {
    return {
      // Selected filter states
      selectedMakes: {},
      selectedModel: {},
      selectedBodyType: {},
      selectedCabType: {},
      selectedVehicleCategory: {},
      selectedFuelType: {},
      selectedSeat: {},
      selectedPowerTrain: {},
      selectedTranmission: {},
      selectedWeeklyBudget: {
        min: null,
        max: 200
      },
      selectedPrice: {
        min: null,
        max: 100000
      },
      // UI state
      isBtnDisabled: true,
      budgetType: REPAYMENT_WEEKLY,
      // Props-derived state (initialized from props)
      isDisabled: this.disabled,
      sortBy: this.sortedBy.value,
      sortOrder: this.sortedOrder,
      // Initialization guard to prevent watchers from firing during setup
      _isInitializing: true
    };
  },
  computed: {
    /**
     * Computed property that combines all filter states
     * Used to watch all filter changes with a single watcher
     */
    _filterStateHash() {
      // Create a hash of all filter states to detect any changes
      // Using JSON.stringify to create a stable hash that changes when any filter changes
      return JSON.stringify({
        selectedMakes: this.selectedMakes,
        selectedModel: this.selectedModel,
        selectedPrice: this.selectedPrice,
        selectedWeeklyBudget: this.selectedWeeklyBudget,
        budgetType: this.budgetType,
        selectedFuelType: this.selectedFuelType,
        selectedBodyType: this.selectedBodyType,
        selectedCabType: this.selectedCabType,
        selectedVehicleCategory: this.selectedVehicleCategory,
        selectedSeat: this.selectedSeat,
        selectedPowerTrain: this.selectedPowerTrain,
        selectedTranmission: this.selectedTranmission
      });
    }
  },
  watch: {
    // Prop watchers
    sortedBy(newValue) {
      this.sortBy = newValue.value;
    },
    sortedOrder(newValue) {
      this.sortOrder = newValue;
    },
    disabled(newValue) {
      this.isDisabled = newValue;
    },
    // Single watcher for all filter changes - enables submit button when any filter changes
    _filterStateHash() {
      // Don't enable button during initialization
      if (!this._isInitializing) {
        this.enableSubmitButton();
      }
    }
  },
  methods: {
    /**
     * Enable the submit button
     */
    enableSubmitButton() {
      this.isBtnDisabled = false;
    },
    /**
     * Disable the submit button
     */
    disableSubmitButton() {
      this.isBtnDisabled = true;
    },
    /**
     * Filter object to return only keys with true values
     * @param {Object} objValue - Object to filter
     * @returns {Array|*} Array of keys with true values, or original value if falsy
     */
    filterResultsWithTrueValues(objValue) {
      if (objValue) {
        return Object.keys(objValue).filter((value) => objValue[value]);
      }
      return objValue;
    },
    /**
     * Format array of values to object with true values
     * @param {Array} values - Array of values to format
     * @returns {Object} Object with values as keys set to true
     */
    formatKeyValueAsTrue(values) {
      let formattedObject = {};
      values.forEach((value) => {
        const make = value.toUpperCase();
        formattedObject = { ...formattedObject, [make]: true };
      });
      return formattedObject;
    },
    /**
     * Initialize selectedMakes from value prop
     */
    getInitialSelectedMakes() {
      const { pair } = this.value;
      let selectedMakes = {};

      if (pair) {
        selectedMakes = {
          ...this.formatKeyValueAsTrue(
            pair.map((value) => {
              const make = value.make.toUpperCase();
              return make;
            })
          )
        };
      }

      if (this.calculatorManufacturers && this.calculatorManufacturers.length > 0) {
        this.calculatorManufacturers.forEach((value) => {
          const make = value.toUpperCase();
          selectedMakes = {
            [make]: false,
            ...selectedMakes
          };
        });
      }

      this.selectedMakes = selectedMakes;
    },
    /**
     * Initialize selectedModels from value prop
     */
    async getInitialSelectedModels() {
      await this.$nextTick();

      this.value?.pair?.forEach(({ make, models }) => {
        if (make && models.length) {
          let normalizedMake = make.toUpperCase();

          if (this.makeModels && this.makeModels.length > 0) {
            const foundMakeModel = this.makeModels.find((item) => item.make.toUpperCase() === make.toUpperCase());
            if (foundMakeModel) {
              normalizedMake = foundMakeModel.make;
            }
          }

          const normalizedModels = models.map((model) => {
            if (this.makeModels && this.makeModels.length > 0) {
              const makeEntry = this.makeModels.find((item) => item.make.toUpperCase() === normalizedMake.toUpperCase());
              if (makeEntry && makeEntry.models) {
                const foundModel = makeEntry.models.find((m) => String(m).toUpperCase() === model.toUpperCase());
                if (foundModel) return foundModel;
              }
            }
            return model.toUpperCase();
          });

          this.$set(this.selectedModel, normalizedMake, normalizedModels);
        }
      });

      await this.$nextTick();
    },
    /**
     * Initialize all filter options from value prop
     */
    async getInitialOptions() {
      // Wait for vehicleCategoryOptions to be available if needed
      if (!this.vehicleCategoryOptions || this.vehicleCategoryOptions.length === 0) {
        await this.$nextTick();
      }
      const {
        vehicleBodytype,
        vehicleFueltype,
        cabType,
        vehicleCategory,
        vehicleMaxRRP,
        vehicleMinRRP,
        vehicleDriveTrain,
        vehicleTransmission,
        vehicleSeats,
        budgetType,
        minBudget,
        maxBudget
      } = this.value;

      if (vehicleBodytype) {
        this.selectedBodyType = this.formatKeyValueAsTrue(vehicleBodytype);
      }

      if (vehicleFueltype) {
        this.selectedFuelType = this.formatKeyValueAsTrue(vehicleFueltype);
      }

      if (cabType) {
        this.selectedCabType = this.formatKeyValueAsTrue(cabType);
      }

      if (vehicleCategory) {
        // vehicleCategory is always an array - ensure it's treated as array
        const categories = Array.isArray(vehicleCategory) ? vehicleCategory : [vehicleCategory];
        // For RefineSearchGroupedCheckboxButton, options have value as arrays like ['SUV SMALL LUXURY', 'SMALL SUV']
        // The component checks: option.value.some((val) => this.selectedOptions[val] === true)
        // So we need to set selectedOptions[val] = true for the EXACT values from option.value
        let formattedCategory = {};

        // Normalize categories from URL for comparison (handle URL encoding like %20 -> space)
        const normalizedCategories = categories.map((cat) => {
          // Decode URL encoding and normalize
          try {
            return decodeURIComponent(cat).toUpperCase().trim();
          } catch {
            return cat.toUpperCase().trim();
          }
        });

        if (this.vehicleCategoryOptions && this.vehicleCategoryOptions.length > 0) {
          // Find options that match any category from URL
          this.vehicleCategoryOptions.forEach((option) => {
            if (Array.isArray(option.value)) {
              // Check if ANY value in this option array matches any category from URL
              const hasMatch = option.value.some((optVal) => {
                const optValNormalized = optVal.toUpperCase().trim();
                return normalizedCategories.includes(optValNormalized);
              });

              if (hasMatch) {
                // Set ALL values in this option array to true
                option.value.forEach((val) => {
                  formattedCategory[val] = true;
                });
              }
            } else {
              // Single value option - use exact value
              const optValNormalized = option.value.toUpperCase().trim();
              if (normalizedCategories.includes(optValNormalized)) {
                formattedCategory[option.value] = true;
              }
            }
          });

          // Also set any categories from URL that weren't matched (fallback)
          normalizedCategories.forEach((cat) => {
            if (!formattedCategory[cat]) {
              formattedCategory[cat] = true;
            }
          });
        } else {
          // Fallback: use formatKeyValueAsTrue if options not available yet
          formattedCategory = this.formatKeyValueAsTrue(categories);
        }

        // Set selectedVehicleCategory using $set to ensure reactivity
        if (Object.keys(formattedCategory).length > 0) {
          Object.keys(formattedCategory).forEach((key) => {
            this.$set(this.selectedVehicleCategory, key, formattedCategory[key]);
          });
        }
      }

      if (vehicleDriveTrain) {
        this.selectedPowerTrain = this.formatKeyValueAsTrue(vehicleDriveTrain);
      }

      if (vehicleTransmission) {
        this.selectedTranmission = this.formatKeyValueAsTrue(vehicleTransmission);
      }

      if (vehicleSeats) {
        this.selectedSeat = this.formatKeyValueAsTrue(vehicleSeats);
      }

      if (vehicleMinRRP && vehicleMaxRRP) {
        this.selectedPrice = {
          min: vehicleMinRRP,
          max: vehicleMaxRRP
        };
      }

      if (minBudget && maxBudget) {
        this.selectedWeeklyBudget = {
          min: minBudget,
          max: maxBudget
        };
      }

      if (budgetType) {
        this.budgetType = budgetType;
      }
    },
    /**
     * Reset all filter states to initial values
     */
    resetLocalState() {
      this.selectedMakes = {};
      this.selectedModel = {};
      this.selectedBodyType = {};
      this.selectedCabType = {};
      this.selectedVehicleCategory = {};
      this.selectedFuelType = {};
      this.selectedSeat = {};
      this.selectedPowerTrain = {};
      this.selectedTranmission = {};
      this.budgetType = REPAYMENT_WEEKLY;
      this.selectedWeeklyBudget = { min: null, max: 200 };
      this.selectedPrice = { min: null, max: 100000 };
      this.isBtnDisabled = true;
    }
  }
};
